import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import moment from "moment";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  formControl: {
    width: "100%",
  },
}));

const emptyForm = {
  type: "income",
  description: "",
  value: "",
  valueDisplay: "",
  dueDate: "",
  status: "pending",
  categoryId: "",
  paymentMethodId: "",
  paidAt: "",
  notes: "",
};

// converte "1.234,56" → 1234.56
const parseBRL = (str) => {
  const clean = str.replace(/\./g, "").replace(",", ".");
  const n = parseFloat(clean);
  return isNaN(n) ? "" : n;
};

// formata dígitos puros (ex: "12345") → "123,45"
const formatBRL = (digits) => {
  const n = parseInt(digits.replace(/\D/g, ""), 10) || 0;
  return (n / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const TransactionModal = ({ open, onClose, transaction, onSave }) => {
  const classes = useStyles();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [methods, setMethods] = useState([]);

  useEffect(() => {
    if (!open) return;

    const loadOptions = async () => {
      try {
        const [catRes, methodRes] = await Promise.all([
          api.get("/transaction-categories"),
          api.get("/payment-methods"),
        ]);
        setCategories(catRes.data);
        setMethods(methodRes.data);
      } catch (err) {
        toastError(err);
      }
    };

    loadOptions();

    if (transaction) {
      const rawValue = transaction.value || 0;
      const digits = Math.round(Number(rawValue) * 100).toString();
      setForm({
        type: transaction.type || "income",
        description: transaction.description || "",
        value: rawValue,
        valueDisplay: formatBRL(digits),
        dueDate: transaction.dueDate || "",
        status: transaction.status || "pending",
        categoryId: transaction.categoryId || "",
        paymentMethodId: transaction.paymentMethodId || "",
        paidAt: transaction.paidAt
          ? transaction.paidAt.substring(0, 16)
          : transaction.status === "paid"
          ? moment().format("YYYY-MM-DDTHH:mm")
          : "",
        notes: transaction.notes || "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, transaction]);

  const filteredCategories = categories.filter((c) => c.type === form.type);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "type") next.categoryId = "";
      if (field === "status") {
        next.paidAt = value === "paid" ? moment().format("YYYY-MM-DDTHH:mm") : "";
      }
      return next;
    });
  };

  const handleValueChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    const display = formatBRL(digits || "0");
    const numeric = parseBRL(display);
    setForm((prev) => ({ ...prev, value: numeric, valueDisplay: display }));
  };

  const handleSubmit = async () => {
    if (!form.description || !form.value || Number(form.value) <= 0 || !form.dueDate) {
      toast.error("Preencha descrição, valor e vencimento.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        value: Number(form.value),
        categoryId: form.categoryId || undefined,
        paymentMethodId: form.paymentMethodId || undefined,
        paidAt: form.paidAt || undefined,
        notes: form.notes || undefined,
      };

      let saved;
      if (transaction?.id) {
        const { data } = await api.put(`/transactions/${transaction.id}`, payload);
        saved = data;
        toast.success("Lançamento atualizado.");
      } else {
        const { data } = await api.post("/transactions", payload);
        saved = data;
        toast.success("Lançamento criado.");
      }

      onSave(saved, !!transaction?.id);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{transaction?.id ? "Editar Lançamento" : "Novo Lançamento"}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl className={classes.formControl}>
              <InputLabel>Tipo</InputLabel>
              <Select value={form.type} onChange={handleChange("type")}>
                <MenuItem value="income">Receita</MenuItem>
                <MenuItem value="expense">Despesa</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl className={classes.formControl}>
              <InputLabel>Status</InputLabel>
              <Select value={form.status} onChange={handleChange("status")}>
                <MenuItem value="pending">Pendente</MenuItem>
                <MenuItem value="paid">Pago</MenuItem>
                <MenuItem value="cancelled">Cancelado</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Descrição"
              fullWidth
              value={form.description}
              onChange={handleChange("description")}
              inputProps={{ maxLength: 255 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Valor (R$)"
              fullWidth
              value={form.valueDisplay}
              onChange={handleValueChange}
              inputProps={{ inputMode: "numeric" }}
              placeholder="0,00"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Vencimento"
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.dueDate}
              onChange={handleChange("dueDate")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl className={classes.formControl}>
              <InputLabel>Categoria</InputLabel>
              <Select value={form.categoryId} onChange={handleChange("categoryId")}>
                <MenuItem value=""><em>Sem categoria</em></MenuItem>
                {filteredCategories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: c.color,
                        marginRight: 8,
                      }}
                    />
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl className={classes.formControl}>
              <InputLabel>Forma de Pagamento</InputLabel>
              <Select value={form.paymentMethodId} onChange={handleChange("paymentMethodId")}>
                <MenuItem value=""><em>Não informado</em></MenuItem>
                {methods.map((m) => (
                  <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {form.status === "paid" && (
            <Grid item xs={12}>
              <TextField
                label="Data do pagamento"
                fullWidth
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={form.paidAt}
                onChange={handleChange("paidAt")}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              label="Observações"
              fullWidth
              multiline
              rows={2}
              value={form.notes}
              onChange={handleChange("notes")}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionModal;
